import type { LocaleObject } from '@nuxtjs/i18n'
import { CZK, DKK, EUR, GBP, HUF, NOK, PLN, RON, SEK, TRY, UAH, USD } from './currencies'

type RemoveIndex<T> = {
  [K in keyof T as string extends K ? never : number extends K ? never : symbol extends K ? never : K]: T[K]
}

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] }

export interface StrictLocaleObject extends WithRequired<Omit<RemoveIndex<LocaleObject>, 'code'>, 'name' | 'dir' | 'language' | 'file'> {
  code: string
  country: string
  currency: string
  /**
   * `circle-flags` is allowed alongside `flagpack` because flagpack ships no
   * European Union flag (its set is countries only), and `eu` needs one.
   */
  flag: `flagpack:${string}` | `circle-flags:${string}`
  borders: string[]
  firstDayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6
  /**
   * What the region picker prints instead of `country`. Only a locale serving
   * more than one country needs it: `country` there is a stand-in picked for
   * the functional uses below, and naming that one country in the picker would
   * tell every other market the storefront is not for them.
   */
  regionLabel?: string
}

export const at: StrictLocaleObject = {
  code: 'at',
  language: 'de-AT',
  name: 'Deutsch (Österreich)',
  dir: 'ltr',
  file: 'de.json',
  country: 'AT',
  currency: EUR.currency,
  flag: 'flagpack:at',
  borders: ['CZ', 'DE', 'HU', 'IT', 'LI', 'SK', 'SI', 'CH'],
  firstDayOfWeek: 1,
}

export const bg: StrictLocaleObject = {
  code: 'bg',
  language: 'bg-BG',
  name: 'български',
  dir: 'ltr',
  file: 'bg.json',
  country: 'BG',
  currency: EUR.currency,
  flag: 'flagpack:bg',
  borders: ['GR', 'MK', 'RO', 'RS', 'TR'],
  firstDayOfWeek: 1,
}

export const ch: StrictLocaleObject = {
  code: 'ch',
  language: 'de-CH',
  name: 'Deutsch (Schweiz)',
  dir: 'ltr',
  file: 'de.json',
  country: 'CH',
  currency: 'CHF',
  flag: 'flagpack:ch',
  borders: ['AT', 'FR', 'IT', 'LI', 'DE'],
  firstDayOfWeek: 1,
}

export const cs: StrictLocaleObject = {
  code: 'cs',
  language: 'cs-CZ',
  name: 'Čeština',
  dir: 'ltr',
  file: 'cs.json',
  country: 'CZ',
  currency: CZK.currency,
  flag: 'flagpack:cz',
  borders: ['AT', 'DE', 'PL', 'SK'],
  firstDayOfWeek: 1,
}

export const de: StrictLocaleObject = {
  code: 'de',
  language: 'de-DE',
  name: 'Deutsch',
  dir: 'ltr',
  file: 'de.json',
  country: 'DE',
  currency: EUR.currency,
  flag: 'flagpack:de',
  borders: ['AT', 'BE', 'CZ', 'DK', 'FR', 'LU', 'NL', 'PL', 'CH'],
  firstDayOfWeek: 1,
}

// Coded by COUNTRY, not language — like `at` above, and unlike `cs`. The
// storefront serves it at /dk/ and the Vendure channel is `dk-channel`, while
// `language` stays `da-DK`: that field is what `localeRequestParams` turns into
// Vendure's LanguageCode, and Vendure has no `dk` (its enum is ISO 639-1).
export const dk: StrictLocaleObject = {
  code: 'dk',
  language: 'da-DK',
  name: 'Dansk',
  dir: 'ltr',
  file: 'da.json',
  country: 'DK',
  // Must match `dk-channel`'s currency: this field is what `usePriceFormatter`
  // labels an amount with when the caller passes no currency.
  currency: DKK.currency,
  flag: 'flagpack:dk',
  borders: ['DE'],
  firstDayOfWeek: 1,
}

export const en: StrictLocaleObject = {
  code: 'en',
  language: 'en-US',
  name: 'English',
  dir: 'ltr',
  file: 'en.json',
  country: 'US',
  currency: USD.currency,
  flag: 'flagpack:us',
  borders: ['CA', 'MX'],
  firstDayOfWeek: 0,
}

export const es: StrictLocaleObject = {
  code: 'es',
  language: 'es-ES',
  name: 'Español',
  dir: 'ltr',
  file: 'es.json',
  country: 'ES',
  currency: EUR.currency,
  flag: 'flagpack:es',
  borders: ['AD', 'FR', 'GI', 'PT', 'MA'],
  firstDayOfWeek: 1,
}

/**
 * English for the euro area — one storefront for every European destination the
 * `en-eu-channel` ships to, rather than one locale per country.
 *
 * `language` is plain `en`, not `en-150`: hreflang only accepts an ISO 3166-1
 * alpha-2 region and `150` is UN M.49, so a region tag here would be ignored
 * outright. Generic `en` is also the honest answer — the storefront targets no
 * single country. It does mean a browser asking for plain `en` (or `en-GB`)
 * now matches this locale rather than `en`/en-US, which is the intended
 * outcome: those shoppers get euro pricing and real European delivery instead
 * of the USD storefront.
 *
 * `country` is IE because three things need a real ISO country and there is no
 * such thing as country "EU": phone validation normalises a local-format number
 * against it (`isValidOrderPhoneNumber`), the cart seeds it as the destination
 * so a fresh basket can be quoted at all (`useFreeShippingProgress`), and the
 * locale map in `localeProximity` is keyed by it. Ireland is the euro country
 * whose language this is, it has courier config on the channel, and it collides
 * with none of the other shipped locales. The picker shows `regionLabel`
 * instead, so no shopper is told this storefront is Irish.
 */
export const eu: StrictLocaleObject = {
  code: 'eu',
  language: 'en',
  name: 'English (Europe)',
  dir: 'ltr',
  file: 'en.json',
  country: 'IE',
  regionLabel: 'EU',
  currency: EUR.currency,
  flag: 'circle-flags:eu',
  // No neighbours: `borders` drives the proximity sort, which orders locales
  // around a single country. This one is not a country.
  borders: [],
  firstDayOfWeek: 1,
}

export const fi: StrictLocaleObject = {
  code: 'fi',
  language: 'fi-FI',
  name: 'Suomi',
  dir: 'ltr',
  file: 'fi.json',
  country: 'FI',
  currency: EUR.currency,
  flag: 'flagpack:fi',
  borders: ['NO', 'SE', 'RU'],
  firstDayOfWeek: 1,
}

export const fr: StrictLocaleObject = {
  code: 'fr',
  language: 'fr-FR',
  name: 'Français',
  dir: 'ltr',
  file: 'fr.json',
  country: 'FR',
  currency: EUR.currency,
  flag: 'flagpack:fr',
  borders: ['AD', 'BE', 'DE', 'IT', 'LU', 'MC', 'ES', 'CH'],
  firstDayOfWeek: 1,
}

export const fr_BE: StrictLocaleObject = {
  code: 'fr-be',
  language: 'fr-BE',
  name: 'Français (Belgique)',
  dir: 'ltr',
  file: 'fr.json',
  country: 'BE',
  currency: EUR.currency,
  flag: 'flagpack:be',
  borders: ['FR', 'DE', 'LU', 'NL'],
  firstDayOfWeek: 1,
}

export const gb: StrictLocaleObject = {
  code: 'gb',
  language: 'en-GB',
  name: 'English (UK)',
  dir: 'ltr',
  file: 'en.json',
  country: 'GB',
  currency: GBP.currency,
  flag: 'flagpack:gb-ukm',
  borders: ['IE'],
  firstDayOfWeek: 1,
}

export const hu: StrictLocaleObject = {
  code: 'hu',
  language: 'hu-HU',
  name: 'Magyar',
  dir: 'ltr',
  file: 'hu.json',
  country: 'HU',
  currency: HUF.currency,
  flag: 'flagpack:hu',
  borders: ['AT', 'HR', 'RO', 'RS', 'SK', 'SI', 'UA'],
  firstDayOfWeek: 1,
}

export const ie: StrictLocaleObject = {
  code: 'ie',
  language: 'en-IE',
  name: 'English (Ireland)',
  dir: 'ltr',
  file: 'en.json',
  country: 'IE',
  currency: EUR.currency,
  flag: 'flagpack:ie',
  borders: ['GB'],
  firstDayOfWeek: 1,
}

export const it: StrictLocaleObject = {
  code: 'it',
  language: 'it-IT',
  name: 'Italiano',
  dir: 'ltr',
  file: 'it.json',
  country: 'IT',
  currency: EUR.currency,
  flag: 'flagpack:it',
  borders: ['AT', 'FR', 'SM', 'SI', 'CH', 'VA'],
  firstDayOfWeek: 1,
}

export const nl: StrictLocaleObject = {
  code: 'nl',
  language: 'nl-NL',
  name: 'Nederlands',
  dir: 'ltr',
  file: 'nl.json',
  country: 'NL',
  currency: EUR.currency,
  flag: 'flagpack:nl',
  borders: ['BE', 'DE'],
  firstDayOfWeek: 1,
}

export const nl_BE: StrictLocaleObject = {
  code: 'nl-be',
  language: 'nl-BE',
  name: 'Nederlands (België)',
  dir: 'ltr',
  file: 'nl.json',
  country: 'BE',
  currency: EUR.currency,
  flag: 'flagpack:be',
  borders: ['FR', 'DE', 'LU', 'NL'],
  firstDayOfWeek: 1,
}

export const no: StrictLocaleObject = {
  code: 'no',
  language: 'nb-NO',
  name: 'Norsk Bokmål',
  dir: 'ltr',
  file: 'nb.json',
  country: 'NO',
  currency: NOK.currency,
  flag: 'flagpack:no',
  borders: ['FI', 'SE', 'RU'],
  firstDayOfWeek: 1,
}

export const pl: StrictLocaleObject = {
  code: 'pl',
  language: 'pl-PL',
  name: 'Polski',
  dir: 'ltr',
  file: 'pl.json',
  country: 'PL',
  currency: PLN.currency,
  flag: 'flagpack:pl',
  borders: ['BY', 'CZ', 'DE', 'LT', 'RU', 'SK', 'UA'],
  firstDayOfWeek: 1,
}

export const pt: StrictLocaleObject = {
  code: 'pt',
  language: 'pt-PT',
  name: 'Português (Portugal)',
  dir: 'ltr',
  file: 'pt-PT.json',
  country: 'PT',
  currency: EUR.currency,
  flag: 'flagpack:pt',
  borders: ['ES'],
  firstDayOfWeek: 1,
}

export const ro: StrictLocaleObject = {
  code: 'ro',
  language: 'ro-RO',
  name: 'Română',
  dir: 'ltr',
  file: 'ro.json',
  country: 'RO',
  currency: RON.currency,
  flag: 'flagpack:ro',
  borders: ['BG', 'HU', 'MD', 'RS', 'UA'],
  firstDayOfWeek: 1,
}

export const sk: StrictLocaleObject = {
  code: 'sk',
  language: 'sk-SK',
  name: 'Slovenčina',
  dir: 'ltr',
  file: 'sk.json',
  country: 'SK',
  currency: EUR.currency,
  flag: 'flagpack:sk',
  borders: ['AT', 'CZ', 'HU', 'PL', 'UA'],
  firstDayOfWeek: 1,
}

export const sv: StrictLocaleObject = {
  code: 'sv',
  language: 'sv-SE',
  name: 'Svenska',
  dir: 'ltr',
  file: 'sv.json',
  country: 'SE',
  currency: SEK.currency,
  flag: 'flagpack:se',
  borders: ['FI', 'NO'],
  firstDayOfWeek: 1,
}

export const tr: StrictLocaleObject = {
  code: 'tr',
  language: 'tr-TR',
  name: 'Türkçe',
  dir: 'ltr',
  file: 'tr.json',
  country: 'TR',
  currency: TRY.currency,
  flag: 'flagpack:tr',
  borders: ['AM', 'AZ', 'BG', 'GE', 'GR', 'IR', 'IQ', 'SY'],
  firstDayOfWeek: 1,
}

export const uk: StrictLocaleObject = {
  code: 'uk',
  language: 'uk-UA',
  name: 'Український',
  dir: 'ltr',
  file: 'uk.json',
  country: 'UA',
  currency: UAH.currency,
  flag: 'flagpack:ua',
  borders: ['BY', 'HU', 'MD', 'PL', 'RO', 'RU', 'SK'],
  firstDayOfWeek: 1,
}
